import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  Stack, Tooltip, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';

const emptyDoc = () => ({ key: '', label: '', required: true });

function DocDialog({ open, onClose, initial, onSave, existingKeys }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(initial || emptyDoc());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setDoc(initial || emptyDoc());
    setErrors({});
  }, [initial, open]);

  const validate = () => {
    const errs = {};
    if (!doc.key.trim()) errs.key = t('docs_key_required');
    else if (!/^[a-z0-9_]+$/.test(doc.key)) errs.key = t('docs_key_invalid');
    else if (existingKeys.includes(doc.key) && doc.key !== initial?.key) errs.key = t('docs_key_exists');
    if (!doc.label.trim()) errs.label = t('docs_label_required');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({ ...doc });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial?.key ? t('docs_edit_doc') : t('docs_add_doc')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('docs_key_label')}
            value={doc.key}
            onChange={e => setDoc(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
            error={!!errors.key}
            helperText={errors.key || t('docs_key_helper')}
            fullWidth
            disabled={!!initial?.key}
          />
          <TextField
            label={t('docs_label_label')}
            value={doc.label}
            onChange={e => setDoc(prev => ({ ...prev, label: e.target.value }))}
            error={!!errors.label}
            helperText={errors.label || t('docs_label_helper')}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={!!doc.required} onChange={e => setDoc(prev => ({ ...prev, required: e.target.checked }))} />}
            label={t('docs_required_label')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button variant="contained" onClick={handleSave}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RequiredDocsSettings() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-documents`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(data => { setDocs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const saveToServer = (updated) => {
    setSaving(true);
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-documents`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ documents: updated })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('settings_saved'), severity: 'success' });
        setSaving(false);
      })
      .catch(() => {
        setSnackbar({ open: true, message: t('settings_save_error'), severity: 'error' });
        setSaving(false);
      });
  };

  const handleSaveDoc = (docData) => {
    let updated;
    if (editingDoc) {
      updated = docs.map(d => d.key === editingDoc.key ? docData : d);
    } else {
      updated = [...docs, docData];
    }
    setDocs(updated);
    saveToServer(updated);
    setDialogOpen(false);
    setEditingDoc(null);
  };

  const handleDeleteConfirm = () => {
    const updated = docs.filter(d => d.key !== deleteTarget.key);
    setDocs(updated);
    saveToServer(updated);
    setDeleteTarget(null);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...docs];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setDocs(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    saveToServer(docs);
  };

  const existingKeys = docs.map(d => d.key);

  if (loading) return <Typography variant="body2" color="text.secondary">{t('loading_text')}</Typography>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('docs_drag_subtitle')}
      </Typography>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {docs.map((doc, idx) => {
          const transKey = `personaldocs_doc_${doc.key}`;
          const translated = t(transKey);
          const displayLabel = (translated && translated !== transKey) ? translated : doc.label;
          return (
            <Paper
              key={doc.key}
              variant="outlined"
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'grab',
                opacity: dragIdx === idx ? 0.5 : 1,
                bgcolor: dragIdx === idx ? 'action.hover' : 'background.paper'
              }}
            >
              <Tooltip title={t('drag_to_reorder')}>
                <DragIndicatorIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
              </Tooltip>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{displayLabel}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>({doc.key})</Typography>
                  {doc.required
                    ? <Chip label={t('required_chip')} size="small" color="primary" variant="outlined" />
                    : <Chip label={t('optional_chip')} size="small" variant="outlined" />
                  }
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={t('edit')}>
                  <IconButton size="small" onClick={() => { setEditingDoc(doc); setDialogOpen(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('delete')}>
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(doc)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => { setEditingDoc(null); setDialogOpen(true); }}
        disabled={saving}
      >
        {t('docs_add_doc')}
      </Button>

      <DocDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingDoc(null); }}
        initial={editingDoc}
        onSave={handleSaveDoc}
        existingKeys={existingKeys}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('confirm_delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('docs_delete_prompt', { label: deleteTarget?.label, key: deleteTarget?.key })}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {t('docs_delete_warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
